import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router';
import TopBar from "../../../../../../component/TopBar";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import { SearchInput, StatCard, StatusChip, personName } from "../../../../../../component/listing";
import { getAllLeavePolicy } from '../../../../../../services/leave';
import { getEmployeeNames } from '../../../../../../utils/getEmployeeNames';
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

const PolicyListing = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllLeavePolicy();
      if (res?.success === false) throw new Error(res?.message || "Failed to load leave policies");
      const employeeIds = (res.data || []).filter((d) => d.createdBy).map((d) => d.createdBy);
      const combinedData = await getEmployeeNames(res.data, employeeIds, "createdBy");
      setData(Array.isArray(combinedData) ? combinedData.reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load leave policies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q));
  }, [data, search]);

  const openEdit = (id) => navigate("/app/leave-policy/edit", { state: { policyId: id } });

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Leave Policy"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total leave policies" value={data.length} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search policy name or code" />
          <BtnPrimary to="settings" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Leave Policy
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load leave policies"
          empty={{
            title: data.length === 0 ? "No leave policies yet" : "No matches",
            description: data.length === 0
              ? "Create a policy to define leave entitlements and accrual."
              : "Try a different search.",
          }}
          onRowClick={(p) => openEdit(p._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (p) => p.code || "—" },
            { key: "name", label: "Policy Name", cellSx: { fontWeight: 600 }, render: (p) => p.name || "—" },
            { key: "creator", label: "Created By", render: (p) => personName(p?.employee) },
            {
              key: "created",
              label: "Created On",
              cellSx: { color: tokens.textMuted },
              render: (p) => (p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-GB") : "—"),
            },
            { key: "types", label: "Leave Types", render: (p) => p?.leaveQuantities?.length || 0 },
            {
              key: "status",
              label: "Status",
              render: (p) =>
                p.status === "draft" ? (
                  <StatusChip label="Draft" tone="warning" />
                ) : (
                  <StatusChip
                    label={p.status === "inactive" ? "Inactive" : "Active"}
                    tone={p.status === "inactive" ? "muted" : "success"}
                  />
                ),
            },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default PolicyListing;
